const finite = (v) => v && [v.x, v.y, v.z].every(Number.isFinite);
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const cross = (a, b) => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});
const unit = (v) => {
  if (!finite(v)) return null;
  const length = Math.hypot(v.x, v.y, v.z);
  return Number.isFinite(length) && length > 1e-9
    ? { x: v.x / length, y: v.y / length, z: v.z / length }
    : null;
};

/** Validate a right-handed rigid flight frame. Origin is world metres; right,
 * up and back are unit directions, with local forward = -Z. No scale/shear or
 * reflection is accepted. Invalid input returns null, never a guessed rotation. */
export function skyFlightFrame(value) {
  if (!value || ![value.origin, value.right, value.up, value.back].every(finite)) return null;
  const { right, up, back } = value;
  if (
    [right, up, back].some((axis) => Math.abs(dot(axis, axis) - 1) > 1e-6) ||
    Math.abs(dot(right, up)) > 1e-6 ||
    Math.abs(dot(right, back)) > 1e-6 ||
    Math.abs(dot(up, back)) > 1e-6 ||
    dot(cross(right, up), back) < 1 - 1e-6
  )
    return null;
  return Object.fromEntries(
    ['origin', 'right', 'up', 'back'].map((key) => [key, { ...value[key] }]),
  );
}

/** Build a frame from an advancing centreline tangent (world metres or unit
 * vector). Project previousRight to transport roll through vertical sections.
 * Degenerate previous directions use the least-aligned Cartesian axis; a zero
 * tangent or nonfinite origin returns null. Inputs are never mutated. */
export function skyFlightFrameFromTangent(origin, tangent, previousRight = { x: 1, y: 0, z: 0 }) {
  const forward = unit(tangent);
  if (!finite(origin) || !forward) return null;
  const back = { x: -forward.x, y: -forward.y, z: -forward.z };
  const project = (axis) => {
    const along = dot(axis, back);
    return unit({
      x: axis.x - along * back.x,
      y: axis.y - along * back.y,
      z: axis.z - along * back.z,
    });
  };
  let right = finite(previousRight) ? project(previousRight) : null;
  if (!right) {
    const axes = [
      { x: 1, y: 0, z: 0 },
      { x: 0, y: 1, z: 0 },
      { x: 0, y: 0, z: 1 },
    ];
    axes.sort((a, b) => Math.abs(dot(a, back)) - Math.abs(dot(b, back)));
    right = project(axes[0]);
  }
  const up = unit(cross(back, right));
  return { origin: { ...origin }, right: unit(cross(up, back)), up, back };
}

/** Rotate a direction/velocity into local axes; units are preserved. Invalid
 * frame or vector returns null so callers can explicitly retain legacy policy. */
export function skyFlightVectorToLocal(frame, vector) {
  const f = skyFlightFrame(frame);
  return f && finite(vector)
    ? { x: dot(vector, f.right), y: dot(vector, f.up), z: dot(vector, f.back) }
    : null;
}
/** Rotate a local direction/velocity to world axes; invalid input returns null. */
export function skyFlightVectorToWorld(frame, vector) {
  const f = skyFlightFrame(frame);
  if (!f || !finite(vector)) return null;
  return Object.fromEntries(
    ['x', 'y', 'z'].map((key) => [
      key,
      f.right[key] * vector.x + f.up[key] * vector.y + f.back[key] * vector.z,
    ]),
  );
}
/** Transform a world point in metres to local metres, or null for invalid data. */
export function skyFlightPointToLocal(frame, point) {
  const f = skyFlightFrame(frame);
  return f && finite(point)
    ? skyFlightVectorToLocal(f, {
        x: point.x - f.origin.x,
        y: point.y - f.origin.y,
        z: point.z - f.origin.z,
      })
    : null;
}
/** Transform a local point in metres to world metres, or null for invalid data. */
export function skyFlightPointToWorld(frame, point) {
  const f = skyFlightFrame(frame),
    vector = skyFlightVectorToWorld(f, point);
  return vector
    ? { x: f.origin.x + vector.x, y: f.origin.y + vector.y, z: f.origin.z + vector.z }
    : null;
}
